import type {
  CorrectionLetterResponse,
  DscWorkbenchDashboardResponseV2,
  ReasonSelection,
} from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchCorrectionLetterApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";
import { correctionLetterKeys } from "./correctionLetterKeys";

/**
 * Mutation hook for saving Correction Letter reason selections against a deal.
 * Backed by `WorkbenchCorrectionLetterApi.saveCorrectionLetter` (POST), which
 * forwards the desired `{ code, selected }` state for each reason to
 * recap-service and returns the refreshed CorrectionLetterResponse. A
 * per-attempt `Idempotency-Key` dedups accidental double-submits. On success the
 * response is written straight into the GET cache, and its `hasCorrectionLetter` is
 * patched into the cached dashboard so the Financial tab's Yes/No updates without a refetch.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveCorrectionLetter(approvalId: string) {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async (reasons: ReasonSelection[]): Promise<CorrectionLetterResponse | undefined> => {
      if (shouldForceExit) {
        return undefined;
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      const response = await workbenchCorrectionLetterApi.saveCorrectionLetter(approvalId, { reasons }, idempotencyKey);
      return response.data;
    },
    onSuccess: (data) => {
      if (!data) {
        return;
      }
      queryClient.setQueryData(correctionLetterKeys.detail({ approvalId }), data);
      // Patch the dashboard's Yes/No in place instead of refetching the whole dashboard for one flag.
      queryClient.setQueryData<DscWorkbenchDashboardResponseV2>(dscWorkbenchKeys.detail({ approvalId }), (dashboard) =>
        dashboard
          ? {
              ...dashboard,
              financial: {
                ...dashboard.financial,
                correctionLetter: {
                  ...dashboard.financial?.correctionLetter,
                  hasCorrectionLetter: data.hasCorrectionLetter,
                },
              },
            }
          : dashboard
      );
    },
  });
}
